import React, { useState, useMemo } from 'react';
import { Card, Table, Btn, Select, Badge } from './UI';
import { fmtDate, fmtNum } from '../utils';

const QSGroupedView = ({ 
  transactions, 
  getBoqMainName, 
  getSubName, 
  getDisciplineName,
  setViewModal,
  setEditModal,
  setDeleteModal
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const groupedTransactions = useMemo(() => {
    const groups = new Map();
    transactions.forEach(t => {
      const boqName = t.boq_item_description || t.boq_component_name || '';
      const boqCode = t.boq_item_code || (getBoqMainName ? getBoqMainName(t.boq_item_id) : '');
      const key = `${boqCode}_${boqName}`;
      
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          boq_code: boqCode,
          boq_desc: boqName,
          discipline: t.discipline || (getDisciplineName ? getDisciplineName(t.discipline_id) : ''),
          records: 0,
          boqQty: Number(t.boq_quantity || 0),
          installed: 0,
          offcut: 0,
          damaged: 0,
          returned: 0,
          wastage: 0,
          lost: 0,
          items: []
        });
      }
      
      const g = groups.get(key);
      g.records += 1;
      g.installed += Number(t.current_installed_qty || 0);
      g.offcut += Number(t.offcut_qty || 0);
      g.damaged += Number(t.lost_qty || 0); 
      g.returned += Number(t.returned_qty || 0);
      g.wastage += Number(t.wastage_qty || 0);
      g.lost += Number(t.lost_qty || 0); 
      
      g.items.push(t);
    });
    
    return Array.from(groups.values()).map(g => {
      g.instPercent = g.boqQty > 0 ? (g.installed / g.boqQty) * 100 : 0;
      g.remaining = g.boqQty - g.installed;
      if (g.remaining < 0) g.remaining = 0;
      return g;
    });
  }, [transactions, getBoqMainName, getDisciplineName]);

  const paginatedGroups = useMemo(() => {
    if (pageSize === 'All') return groupedTransactions;
    const start = (page - 1) * pageSize;
    return groupedTransactions.slice(start, start + pageSize);
  }, [groupedTransactions, page, pageSize]);

  const innerCols = [
    { align: 'center', key: 'sn', label: '#', width: 40, render: (_, __, idx) => <span style={{ fontSize: 11, color: '#94a3b8' }}>{idx + 1}</span> },
    { align: 'center', key: 'installation_date', label: 'DATE', width: 90, render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(v)}</span> },
    { align: 'center', key: 'location_installed', label: 'LOCATION INSTALLED', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.location || '—'}</span> },
    { align: 'center', key: 'zone', label: 'BUILDING / ZONE', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.zone || '—'}</span> },
    { align: 'center', key: 'section', label: 'SECTION', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.dmr_sub_section || (getSubName ? getSubName(row.dmr_subsection_id) : '—')}</span> },
    { align: 'center', key: 'brand_name', label: 'BRAND', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
    { align: 'center', key: 'unit_of_measure', label: 'UNIT', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
    
    { align: 'center', key: 'boq_quantity', label: 'BOQ QTY', render: v => <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
    { align: 'center', key: 'previous_installed_qty', label: 'PREV INST', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
    { align: 'center', key: 'current_installed_qty', label: 'INST QTY', render: v => <span style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
    { align: 'center', key: 'total_installed_qty', label: 'TOTAL INST', render: v => <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
    { align: 'center', key: 'remaining_qty', label: 'REMAINING', render: v => <span style={{ fontSize: 11, color: '#d97706', whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
    { align: 'center', key: 'installation_percentage', label: 'INST%', render: v => <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{v ? `${Number(v).toFixed(1)}%` : '—'}</span> },
    
    { align: 'center', key: 'offcut_qty', label: 'OFFCUT', render: v => <span style={{ fontSize: 11, color: 'var(--brand)', whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
    { align: 'center', key: 'damaged', label: 'DAMAGED', render: (v, row) => <span style={{ fontSize: 11, color: '#dc2626', whiteSpace: 'nowrap' }}>{fmtNum(0)}</span> },
    { align: 'center', key: 'returned_qty', label: 'RETURNED', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
    { align: 'center', key: 'wastage_qty', label: 'WASTAGE', render: v => <span style={{ fontSize: 11, color: '#d97706', whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
    { align: 'center', key: 'lost_qty', label: 'LOST', render: v => <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
    
    { align: 'center', key: 'installer_name', label: 'INSTALLER', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
    { align: 'center', key: 'supervisor_name', label: 'SUPERVISOR', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || (row.supervisor ? row.supervisor.first_name + ' ' + row.supervisor.last_name : '—')}</span> },
    
    { align: 'center', key: 'qa_qc_status', label: 'QA/QC', render: v => v ? <Badge color={v.toLowerCase() === 'passed' ? 'green' : 'amber'}>{v}</Badge> : '—' },
    {
      align: 'center', key: 'approval_status', label: 'APPROVAL', render: v => {
        let color = 'blue';
        if (v === 'Draft') color = 'gray';
        if (v === 'Approved') color = 'green';
        return v ? <Badge color={color}>{v}</Badge> : '—';
      }
    },
    {
      key: 'actions', label: 'ACT', render: (_, row) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          <Btn variant="ghost" size="sm" onClick={() => setViewModal && setViewModal(row)} title="View" style={{ padding: '4px 6px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => setEditModal && setEditModal(row)} title="Edit" style={{ padding: '4px 6px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </Btn>
        </div>
      )
    }
  ];

  return (
    <Card style={{ border: '1px solid black', borderTop: '1px solid black', overflow: 'hidden', borderRadius: 8, borderTopLeftRadius: 0 }}>
      <div style={{ background: 'var(--bg-elevated)', minHeight: 400 }}>
        
        {paginatedGroups.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
            No grouped data available for the selected filters.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {paginatedGroups.map(group => {
              const isExp = expandedGroups[group.id];
              return (
                <div key={group.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* GROUP HEADER CARD */}
                  <div 
                    onClick={() => toggleGroup(group.id)}
                    style={{ 
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                      padding: '10px 16px', cursor: 'pointer', background: isExp ? '#f8fafc' : 'white',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={e => { if(!isExp) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if(!isExp) e.currentTarget.style.background = 'white'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 14, color: '#0b318bff', transform: isExp ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>
                        ›
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 800, color: '#0b318bff' }}>{group.boq_code}</span>
                      {group.discipline && <Badge color="gray">{group.discipline}</Badge>}
                      <span style={{ fontSize: 11, color: '#475569' }}>{group.boq_desc}</span>
                    </div>

                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      {[
                        { label: 'Records', val: fmtNum(group.records) },
                        { label: 'BOQ Qty', val: fmtNum(group.boqQty) },
                        { label: 'Installed', val: fmtNum(group.installed), color: '#16a34a' },
                        { label: 'Inst%', val: `${group.instPercent.toFixed(1)}%`, color: '#ea580c' },
                        { label: 'Offcut', val: fmtNum(group.offcut), color: '#2563eb' },
                        { label: 'Damaged', val: fmtNum(group.damaged), color: '#dc2626' }
                      ].map((stat, i) => (
                        <div key={i} style={{ textAlign: 'center', minWidth: 40 }}>
                          <div style={{ fontSize: 8.5, color: '#94a3b8', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>{stat.label}</div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: stat.color || '#1e293b' }}>{stat.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* EXPANDED INNER TABLE */}
                  {isExp && (
                    <div style={{ padding: '0 0 20px 0', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ overflowX: 'auto', margin: 0, borderBottom: '1px solid #e2e8f0' }}>
                        <Table 
                          cols={innerCols} 
                          rows={group.items.map(i => ({...i, _bgColor: 'white'}))} 
                          emptyText="No items in this group."
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
      
      {/* PAGINATION CONTROLS */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Show</span>
          <Select value={pageSize} onChange={v => { setPageSize(v === 'All' ? 'All' : Number(v)); setPage(1); }} options={[
            { value: 10, label: '10' },
            { value: 25, label: '25' },
            { value: 50, label: '50' },
            { value: 100, label: '100' },
            { value: 'All', label: 'All' }
          ]} style={{ minWidth: 80 }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>groups</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {(() => {
              const totalCount = groupedTransactions.length;
              const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
              const end = pageSize === 'All' ? totalCount : Math.min(page * pageSize, totalCount);
              return `Showing ${start} to ${end} of ${totalCount} groups`;
            })()}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="secondary" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Btn>
            <Btn variant="secondary" disabled={pageSize === 'All' || page * pageSize >= groupedTransactions.length} onClick={() => setPage(p => p + 1)}>Next</Btn>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default QSGroupedView;
